<template>
  <div class="">
    <!-- Header and Add Button -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
      <div class="flex items-center gap-2">
        <!-- material back button -->
        <button @click="goBack" class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 rounded p-1 transition text-white">
          <span class="material-icons text-base">arrow_back</span>
        </button>
        <div>
          <h2 class="text-xl font-semibold text-gray-800">
            {{ selectedCategory && selectedCategory.name ? `List of articles under ${selectedCategory.name}` : "List of articles" }}
          </h2>
        </div>
      </div>
      <button
        @click="addingNewItem"
        class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-2 rounded-lg shadow transition"
      >
        <span class="material-icons text-base">add</span>
        Add Article
      </button>
    </div>
    <!-- Filter Bar -->
    <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
      <div class="flex flex-col gap-1">
        <div class="flex bg-gray-200 rounded-lg">
          <button
            :class="[
              'px-4 py-2 font-medium focus:outline-none transition select-none',
              search.status === '0' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
            ]"
            @click="
              search.status = '0';
              searchData();
            "
          >
            All
          </button>
          <button
            :class="[
              'px-4 py-2 font-medium focus:outline-none transition select-none',
              search.status === '1' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
            ]"
            @click="
              search.status = '1';
              searchData();
            "
          >
            Active
          </button>
          <button
            :class="[
              'px-4 py-2 font-medium focus:outline-none transition select-none',
              search.status === '2' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
            ]"
            @click="
              search.status = '2';
              searchData();
            "
          >
            Inactive
          </button>
        </div>
        <p v-if="search.combineViews" class="text-sm text-gray-500 mb-0">(Views combined across all languages)</p>
      </div>

      <!--  Search and Sort Controls -->
      <div class="flex items-center gap-2">
        <vs-select v-model="search.language" @change="searchData" class="w-28" :color="'#6366f1'">
          <vs-select-item value="all" text="Language" disabled />
          <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
        </vs-select>
        <vs-select v-model="search.sort" @change="searchData" class="w-28" :color="'#6366f1'">
          <vs-select-item value="ASC" text="Ascending" />
          <vs-select-item value="DESC" text="Descending" />
        </vs-select>
        <!-- combine views -->
        <vs-checkbox v-model="search.combineViews" @change="searchData" class="ml-2" :color="'#6366f1'"> Combine Views </vs-checkbox>
        <button
          @click="sortPositions()"
          class="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">sort</span> Sort
        </button>
        <button
          @click="resetData()"
          class="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">refresh</span> Reset
        </button>
      </div>
    </div>
    <!-- Table -->
    <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white pb-2">
      <table class="min-w-full bg-white rounded-xl">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th class="px-4 py-3"></th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase cursor-pointer" @click="sortBy('position')">
              Position
              <span v-if="sortByField === 'position'">
                {{ sortDirection === "asc" ? "▲" : "▼" }}
              </span>
            </th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Language</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase cursor-pointer" @click="sortBy('status')">
              Status
              <span v-if="sortByField === 'status'">
                {{ sortDirection === "asc" ? "▲" : "▼" }}
              </span>
            </th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase cursor-pointer" @click="sortBy('totalViewCount')">
              Views
              <span v-if="sortByField === 'totalViewCount'">
                {{ sortDirection === "asc" ? "▲" : "▼" }}
              </span>
            </th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase cursor-pointer" @click="sortBy('publishDate')">
              Publish Date
              <span v-if="sortByField === 'publishDate'">
                {{ sortDirection === "asc" ? "▲" : "▼" }}
              </span>
            </th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(article, idx) in articleList" :key="article._id" class="border-b hover:bg-gray-100 transition">
            <td class="px-4 py-1">{{ idx + 1 + (currentPage - 1) * limit }}</td>
            <td class="">
              <img
                :src="article.image || require('@/assets/images/No-image.png')"
                class="rounded-md object-cover"
                style="width: 90px; height: 60px"
              />
            </td>
            <td class="px-4 py-1 font-medium text-gray-800">{{ article.name }}</td>
            <td class="px-4 py-1 font-medium text-gray-800">
              <span class="material-icons" style="cursor: pointer" @click="viewDesc(article)">remove_red_eye</span>
            </td>
            <td class="px-4 py-1 text-center">
              <div class="flex flex-col items-center gap-2">
                <button
                  class="flex items-center justify-center border-2 border-gray-100 rounded-md w-6 h-6 disabled:opacity-50"
                  :disabled="article.position === 1"
                  @click.stop="updatePosition(article, search.sort === 'ASC' ? 'up' : 'down')"
                >
                  <svg
                    :class="article.position === 1 ? 'text-gray-200' : 'text-gray-400'"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-10 h-10"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 15l-7-7-7 7" />
                  </svg>
                </button>
                <span class="font-semibold text-gray-700">{{ article.position }}</span>
                <button
                  class="flex items-center justify-center border-2 border-gray-100 rounded-md w-6 h-6 disabled:opacity-50"
                  :disabled="article.position === articleList.length"
                  @click.stop="updatePosition(article, search.sort === 'ASC' ? 'down' : 'up')"
                >
                  <svg
                    :class="article.position === articleList.length ? 'text-gray-200' : 'text-gray-400'"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-10 h-10"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </td>
            <td
              class="px-4 py-1 text-center"
              :title="article.missingLanguages.length > 0 ? `Missing: ${article.missingLanguages.join(', ')}` : 'All languages available'"
            >
              <div class="flex items-center justify-center gap-1">
                <span class="text-gray-500 text-lg">{{ article.language }}</span>
                <span
                  v-if="article.languageCount && article.missingLanguages && article.language === 'EN'"
                  :class="[
                    'inline-flex items-center px-2 py-1 rounded-full font-semibold cursor-pointer',
                    article.languageCount - article.missingLanguages.length !== 11 ? 'bg-red-900 text-white' : 'bg-blue-900 text-white',
                  ]"
                >
                  {{ article.languageCount - article.missingLanguages.length }}/{{ article.languageCount }}
                </span>
                <span v-if="article.language === 'EN'" class="material-icons text-gray-400 cursor-pointer" style="font-size: 20px">info</span>
              </div>
            </td>
            <td class="px-4 py-1 text-center">
              <span
                :class="article.status ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'"
                class="inline-block px-3 py-1 rounded-full font-semibold"
              >
                {{ article.status ? "Active" : "Inactive" }}
              </span>
            </td>
            <td class="px-4 py-1 text-center font-semibold text-gray-700">{{ article.totalViewCount || 0 }}</td>
            <td class="px-4 py-1 text-center font-medium text-gray-800">
              {{ formatDate(article.publishDate) }}
              <span v-if="article.publishTime" style="font-size: 0.8rem; font-weight: 600">at {{ article.publishTime }}</span>
            </td>
            <td class="px-4 py-1">
              <vs-dropdown>
                <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
                <vs-dropdown-menu>
                  <!-- disalbe all -->
                  <vs-dropdown-item @click="updateStatus(article._id, !article.status)" v-if="search.language === 'EN'">
                    <vs-icon :icon="article.status ? 'check_box' : 'check_box_outline_blank'" class="align-right" />
                    {{ article.status ? "Disable All" : "Enable All" }}
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="editingItem(article)"> <vs-icon icon="create" class="align-right" /> Edit </vs-dropdown-item>
                  <vs-dropdown-item
                    @click="
                      itemToDelete = article;
                      deleteArticlePopup = true;
                    "
                  >
                    <vs-icon icon="delete_sweep" /> Delete
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </td>
          </tr>
          <tr v-if="!articleList || articleList.length === 0">
            <td colspan="12" class="text-center h-full py-6 text-gray-400">No articles found.</td>
          </tr>
        </tbody>
      </table>
      <!-- Pagination -->
      <div class="flex flex-col md:flex-row items-center justify-between mt-3 pr-3">
        <div class="invisible">
          <span>asd</span>
        </div>
        <div class="flex justify-center items-center">
          <vs-pagination v-model="currentPage" :total="totalPages" :limit="1" @change="searchData" class="my-2" />
        </div>
        <div class="flex items-center gap-2 text-sm mt-2 md:mt-0">
          <span>Show Records:</span>
          <vs-select v-model="limit" @change="searchData" class="w-20" :color="'#6366f1'">
            <vs-select-item value="25" text="25" />
            <vs-select-item value="50" text="50" />
            <vs-select-item value="100" text="100" />
          </vs-select>
        </div>
      </div>
    </div>
    <!-- Delete Confirmation Popup -->
    <vs-popup title="Delete Article" :active.sync="deleteArticlePopup">
      <p class="mb-4 text-lg text-gray-700">Are you sure you want to delete this article?</p>
      <div class="flex justify-end gap-2">
        <vs-button color="warning" @click="deleteCategory">Delete</vs-button>
        <vs-button color="primary" @click="deleteArticlePopup = false">Cancel</vs-button>
      </div>
    </vs-popup>
    <!-- desc popup -->
    <vs-popup title="View Article Description" :active.sync="viewPopup">
      <div v-html="desc"></div>
    </vs-popup>
    <!-- Add Article Popup -->
    <vs-popup :title="isNew ? 'Add New Article' : clone ? 'Duplicate Article' : 'Edit Article'" :active.sync="defaultalert">
      <div class="flex flex-col gap-1 px-1">
        <div class="flex justify-between mb-2">
          <div class="w-1/4" v-if="!isNew">
            <label class="block text-sm font-semibold mb-2">Language</label>
            <vs-select v-model="cat.language" @change="fileterusingLanguage($event, cat)" class="w-full" :color="'#6366f1'" :disabled="showLoader">
              <vs-select-item value="" text="Select Language" disabled />
              <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
            </vs-select>
          </div>
          <div v-if="!isNew && cat.language === 'EN'" :title="'Toggle to update existing translations'">
            <!-- toggle pill to update existing translations -->
            <label class="block text-sm font-semibold mb-2">Update Translations</label>
            <toggle-pill v-model="cat.translate" :disabled="showLoader" />
          </div>
        </div>
        <!-- title -->
        <label class="block text-sm font-semibold mb-2">Article Title*</label>
        <vs-input v-model="cat.name" placeholder="Keep short and simple" class="w-full" required :disabled="showLoader" />
        <!-- article desc blocks -->
        <div class="flex-1 w-full" v-if="cat.descriptionBlocks && cat.descriptionBlocks.length > 0">
          <label class="block text-sm font-semibold mb-2">
            <span class="flex w-full justify-between">
              <span> Article Description*: </span>
              <span> {{ cat.descriptionBlocks && cat.descriptionBlocks.length }}/4 (one block required) </span>
            </span>
          </label>

          <vs-row v-for="(block, index) in cat.descriptionBlocks" :key="index" class="article-block mb-3">
            <vs-col vs-lg="14" vs-xs="14" style="border: 1px solid #ccc; padding-top: 1rem; padding-bottom: 1rem">
              <div style="display: flex; justify-content: space-between; align-items: center">
                <label>Block - {{ index + 1 }}</label>
                <vs-button
                  v-if="cat.descriptionBlocks && cat.descriptionBlocks.length > 1"
                  icon="delete"
                  @click="removeBlock(index)"
                  style="border-radius: 50%; z-index: 0"
                  color="danger"
                />
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 1rem">
                <!-- ask for native ad/shop item ad -->
                <vs-select
                  :color="colorx"
                  label="Ad Type"
                  v-model="block.adType"
                  class="mb-2"
                  @change="(block.image = ''), (block.adUrl = ''), (block.shopItem = '')"
                >
                  <vs-select-item :value="item.value" :text="item.text" v-for="item in adTypes" />
                </vs-select>
                <!-- Only show the image upload option when AdOwnImage is selected -->
                <div class="upload-btn-wrapper vs-input-primary" v-if="block.adType == 'AdOwnImage'" style="width: 50%">
                  <label class="vs-input--label">Ad image</label>
                  <div class="btn mt-2"><vs-icon icon="cloud_upload" class="icon-medium"></vs-icon>Upload a File</div>
                  <input type="file" @change="uploadImageAd($event, index)" />
                </div>
                <!-- show shop item selection if its adOwnImage -->
                <div v-if="block.adType == 'Shop'" style="width: 50%">
                  <label>Shop Item</label>
                  <multiselect
                    v-model="block.shopItem"
                    placeholder="Select Item"
                    label="name"
                    track-by="value"
                    :options="selectedItems"
                    :multiple="false"
                    @input="updateAdDisplayImage(block.shopItem, index)"
                  >
                    <template #singleLabel="{ option }">
                      {{ truncateName(option.name) }}
                    </template>
                  </multiselect>
                </div>
              </div>
              <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 1rem">
                <vs-col vs-lg="12" vs-xs="12">
                  <!-- remove image -->
                  <vs-button v-if="block.image" color="danger" type="flat" icon="clear" class="closeBtn" @click="block.image = ''"></vs-button>
                  <!-- show image if present -->
                  <img v-if="block.image" :src="block.image" class="imgUp" height="100px" width="150px" />
                </vs-col>
                <vs-input
                  label="URL"
                  v-model="block.adUrl"
                  v-if="block.adType == 'AdOwnImage' || block.adType == 'Shop'"
                  :disabled="block.adType == 'Shop'"
                />
              </div>
              <!-- block n -->
              <vs-col vs-lg="14" vs-xs="14">
                <label>Description</label>
                <vue-editor v-model="block.description" :editor-toolbar="customToolbar"></vue-editor>
              </vs-col>
            </vs-col>
          </vs-row>
          <!-- new block button -->
          <div class="flex w-full items-center justify-center mb-2">
            <vs-button class="w-2/6" color="primary" @click="addBlock" v-if="cat.descriptionBlocks.length < 4"> + Add Block </vs-button>
          </div>
        </div>
        <vs-col vs-lg="12" vs-xs="12" class="mt-4" v-else>
          <label>Description</label>
          <vue-editor v-model="cat.description"></vue-editor>
        </vs-col>
        <!-- <vs-col vs-lg="12" vs-xs="12" class="mb15 mt20">
              <uploader
                label="Category File (Try to use square image)"
                v-model="cat.image"
                :file="cat.image"
              />
            </vs-col> -->
        <div class="flex w-full justify-between mb-3">
          <div class="flex flex-col items-start">
            <label class="block text-sm font-semibold">
              Position
              <span v-if="!isNew" class="font-bold">: {{ cat.position ? cat.position : "Not set" }}</span>
            </label>
            <div class="flex gap-4">
              <vs-checkbox v-model="cat.moveTop" @change="onMoveTopChange" :disabled="showLoader">Move to Top</vs-checkbox>
              <vs-checkbox v-model="cat.moveBottom" @change="onMoveBottomChange" :disabled="showLoader">Move to Bottom</vs-checkbox>
            </div>
          </div>
          <div class="flex flex-col items-start">
            <label class="block text-sm font-semibold"> Status </label>
            <vs-checkbox v-model="cat.status" :disabled="showLoader">(Active / Inactive)</vs-checkbox>
          </div>
          <div class="flex flex-col items-start">
            <label class="block text-sm font-semibold"> Notification </label>
            <vs-checkbox v-model="cat.notification" :disabled="showLoader">(Active / Inactive)</vs-checkbox>
          </div>
        </div>

        <div class="flex flex-col md:flex-row md:items-center md:gap-6 flex-1">
          <div class="flex-1" style="width: 100px; height: 150px">
            <div class="flex justify-between items-start">
              <!-- image -->
              <label class="block text-sm font-semibold">Article Image</label>
              <button
                v-if="displayImage"
                type="button"
                color="danger"
                class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
                @click="clearImage"
                :disabled="showLoader"
              >
                Clear
              </button>
            </div>
            <!-- image -->
            <div
              class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
              style="width: 100%; height: 140px; min-height: 140px"
              @dragover.prevent
              @dragenter.prevent
              @drop.prevent="onDropImage"
              @click="triggerFileInput"
            >
              <img v-if="displayImage" :src="displayImage" class="rounded-md object-cover border" style="width: 200px; max-height: 140px" />
              <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
                <span class="material-icons" style="font-size: 32px">cloud_upload</span>
                <span>Drag & Drop or Click to Upload</span>
              </div>
              <input
                type="file"
                ref="fileInput"
                class="hidden"
                @change="onImageChange"
                accept="image/jpeg,image/jpg,image/webp"
                :disabled="showLoader"
              />
            </div>
          </div>
          <!-- feature image -->
          <div class="flex-1" style="width: 100px; height: 150px">
            <div class="flex justify-between items-center">
              <label class="block text-sm font-semibold">Notification Image</label>
              <button
                v-if="cat.notificationImage"
                type="button"
                color="danger"
                class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
                @click="clearNotificationImage"
                :disabled="showLoader"
              >
                Clear
              </button>
            </div>
            <div
              class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
              style="width: 100%; height: 140px; min-height: 140px"
              @dragover.prevent
              @dragenter.prevent
              @drop.prevent="onDropImageNotification"
              @click="triggerNotificationFileInput"
            >
              <img v-if="notificationImage" :src="notificationImage" class="rounded-md object-cover border" style="width: 200px; max-height: 140px" />
              <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
                <span class="material-icons" style="font-size: 32px">cloud_upload</span>
                <span>Drag & Drop or Click to Upload</span>
              </div>
              <input
                type="file"
                ref="featureFileInput"
                class="hidden"
                @change="onNotificationImageChange"
                accept="image/jpeg,image/jpg,image/webp"
                :disabled="showLoader"
              />
            </div>
          </div>
        </div>

        <div class="flex-1 mt-6 mb-2">
          <label class="block text-sm font-semibold">Related Articles</label>
          <multiselect
            v-model="cat.relatedArticles"
            placeholder="Select Articles"
            label="name"
            track-by="_id"
            :options="languageArticleList"
            :multiple="true"
            style="z-index: 2"
          ></multiselect>
        </div>

        <div class="flex w-full justify-between">
          <div class="flex flex-col items-start">
            <label class="block text-sm font-semibold">Publish Date</label>
            <datepicker
              placeholder="Select Date"
              v-model="cat.publishDate"
              format="yyyy-MM-dd"
              input-class="border z-10"
              :open-date="new Date()"
            ></datepicker>
          </div>
          <div class="flex flex-col items-start">
            <label class="block text-sm font-semibold">Publish Time</label>
            <vue-timepicker v-model="cat.publishTime" format="hh:mm A" :minute-interval="15" />
          </div>
        </div>

        <div class="flex flex-col justify-between">
          <img v-if="this.$data.cat.authorImage !== ''" :src="cat.authorImage" class="imgUp" height="100px" width="150px" />
          <p class="block text-sm font-semibold">Author: {{ this.$data.cat.authorName || "No Author" }}</p>

          <!-- If author is already assigned -->
          <div v-if="this.$data.cat.authorName && !isNew">
            <vs-button color="danger" @click="clearAuthor">Clear</vs-button>
          </div>

          <!-- If no author and !isNew (edit mode) -->
          <div v-if="!isNew && !this.$data.cat.authorName">
            <vs-button @click="assignYourselfAsAuthor">Assign yourself</vs-button>
          </div>
        </div>
        <!-- independent language to translate -->
        <div v-if="isNew && cat.language === 'EN'">
          <label class="block text-sm font-semibold mb-2">Choose Languages to Translate</label>
          <div class="grid grid-cols-7 gap-2">
            <vs-checkbox v-model="selectAllLang" @change="toggleSelectAll" :disabled="showLoader" class="z-0">All</vs-checkbox>
            <div v-for="lang in languages" :key="lang.culture">
              <vs-checkbox
                :value="cat.languagesToTranslate.includes(lang.culture)"
                @change="toggleLanguage(lang.culture)"
                class="z-0"
                :disabled="lang.culture === 'EN' || showLoader"
              >
                {{ lang.culture }}
              </vs-checkbox>
            </div>
          </div>
        </div>
        <!-- add missing languages -->
        <div v-if="!isNew && cat.language === 'EN' && cat.missingLanguages && cat.missingLanguages.length > 0">
          <label class="block text-sm font-semibold mb-2">Add missing translations</label>
          <vs-checkbox v-model="cat.translateMissing" :disabled="showLoader">Add missing translations</vs-checkbox>
          <div v-if="cat.translateMissing" class="grid grid-cols-7 gap-2 mt-3">
            <vs-checkbox v-model="selectAllEdit" @change="toggleSelectAllEdit" :disabled="showLoader" class="z-0">All</vs-checkbox>
            <div v-for="lang in cat.missingLanguages" :key="lang">
              <vs-checkbox
                :value="cat.languagesToTranslate.includes(lang) && lang !== 'EN'"
                :disabled="lang === 'EN' || showLoader"
                class="z-0"
                @change="toggleLanguage(lang)"
              >
                {{ lang }}
              </vs-checkbox>
            </div>
          </div>
        </div>

        <div class="flex w-full mt-2 z-0">
          <vs-button icon="save" class="w-full" @click="create" v-if="!cat.translateMissing">{{
            isNew && cat.languagesToTranslate.length > 1 ? "Save And Translate" : isNew ? "Save English" : "Update Current"
          }}</vs-button>
          <!-- hide if article is editing -->
          <!-- <vs-button v-if="isNew" class="ml-3 w-full" icon="save" @click="createAndTranslate">Save and Translate</vs-button> -->
          <!-- show only if article is editing and language is english -->
          <vs-button v-if="!showLoader && !isNew && cat.language == 'EN'" class="ml-3 w-full" icon="save" @click="createAndTranslate">{{
            cat.translateMissing ? "Update And Translate Missing" : "Update All"
          }}</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import api from "@/rest.js";
import { API } from "@/common/constants";
import TogglePill from "../components/Common/TogglePill.vue";
import Multiselect from "vue-multiselect";
import { VueEditor, Quill } from "vue2-editor";
import Datepicker from "vuejs-datepicker";
import VueTimepicker from "vue2-timepicker";
import "vue2-timepicker/dist/VueTimepicker.css";
import "vue2-datepicker/index.css";
import moment from "moment";
import Vue from "vue";
import VPopover from "vue-js-popover";

Vue.use(VPopover, { tooltip: true });

function getFontName(font) {
  return font.toLowerCase().replace(/\s/g, "-");
}

const fontList = ["Onest", "Arial"];
const fontNames = fontList.map((font) => getFontName(font));
const fonts = Quill.import("formats/font");

fonts.whitelist = fontNames;
Quill.register(fonts, true);

let fontStyles = "";
fontList.forEach(function (font) {
  let fontName = getFontName(font);
  fontStyles +=
    ".ql-snow .ql-picker.ql-font .ql-picker-label[data-value=" +
    fontName +
    "]::before, .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=" +
    fontName +
    "]::before {" +
    "content: '" +
    font +
    "';" +
    "font-family: '" +
    font +
    "', sans-serif;" +
    "}" +
    ".ql-font-" +
    fontName +
    "{" +
    " font-family: '" +
    font +
    "', sans-serif;" +
    "}";
});

const node = document.createElement("style");
node.innerHTML = fontStyles;
document.body.appendChild(node);

export default {
  name: "ArticleList",
  components: {
    TogglePill,
    Multiselect,
    VueEditor,
    Datepicker,
    VueTimepicker,
  },
  data: () => ({
    title: "Article",
    colorx: "#0D2144",
    limit: 25,
    currentPage: 1,
    totalPages: 0,
    defaultalert: false,
    deleteArticlePopup: false,
    viewPopup: false,
    desc: "",
    isNew: true,
    clone: false,
    displayImage: "",
    notificationImage: "",
    fileName: "",
    notificationfileName: "",
    authorImageFileName: "",
    sortDirection: "asc",
    sortByField: "position",
    search: {
      sort: "ASC",
      language: "EN",
      status: "0",
      combineViews: false,
    },
    adTypes: [
      { value: "", text: "none" },
      { value: "Native", text: "Native Ad" },
      { value: "Shop", text: "Shop Ad" },
      { value: "AdOwnImage", text: "Custom Image Ad" },
    ],
    cat: {
      status: true,
      language: "",
      position: "",
      name: "",
      image: "",
      relatedArticles: [],
      notification: true,
      publishDate: "",
      publishTime: null,
      descriptionBlocks: [{ adType: "", description: "", image: "", adUrl: "" }],
      notificationImage: "",
      articlecategory: "",
      // relatedItems: [],
      moveTop: false,
      moveBottom: false,
      authorName: "",
      authorImage: "",
      languagesToTranslate: [],
      translate: false,
      translateMissing: false,
      missingLanguages: [],
    },
    selectAllLang: false,
    selectAllEdit: false,
    selectedItems: [],
    itemToDelete: "",
    itemToUpdate: "",
    languages: [],
    articleList: [],
    languageArticleList: [],
    selectedCategory: {},
    cat2: "",
    showLoader: false,
    customToolbar: [
      [{ header: [false, 1, 2, 3, 4, 5, 6] }],
      ["bold", "italic", "underline", "strike"],
      [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ color: [] }, { background: [] }],
      ["link", "image", "video"],
      ["clean"],
      [{ font: fonts.whitelist }],
    ],
  }),
  computed: {
    sortedData() {
      return [...this.articleList].sort((a, b) => {
        const fieldA = a[this.sortByField];
        const fieldB = b[this.sortByField];

        if (this.sortByField === "publishDate") {
          return this.sortDirection === "asc" ? new Date(fieldA) - new Date(fieldB) : new Date(fieldB) - new Date(fieldA);
        }

        if (typeof fieldA === "string") {
          return this.sortDirection === "asc" ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
        }

        return this.sortDirection === "asc" ? fieldA - fieldB : fieldB - fieldA;
      });
    },
  },
  methods: {
    toggleSelectAll() {
      if (this.selectAllLang) {
        this.cat.languagesToTranslate = this.languages.map((lang) => lang.culture);
      } else {
        this.cat.languagesToTranslate = ["EN"];
      }
    },

    toggleSelectAllEdit() {
      if (this.selectAllEdit) {
        this.cat.languagesToTranslate = this.cat.missingLanguages;
      } else {
        this.cat.languagesToTranslate = ["EN"];
      }
    },

    toggleLanguage(culture) {
      const selectedLanguages = this.cat.languagesToTranslate;

      if (selectedLanguages.includes(culture)) {
        this.cat.languagesToTranslate = selectedLanguages.filter((lang) => lang !== culture);
      } else {
        this.cat.languagesToTranslate = [...selectedLanguages, culture];
      }

      const allLanguagesSelected = this.languages.every((lang) => this.cat.languagesToTranslate.includes(lang.culture));
      this.selectAllLang = allLanguagesSelected;
      if (this.selectAllEdit) {
        const allLanguagesSelected = this.cat.missingLanguages.every((lang) => this.cat.languagesToTranslate.includes(lang));
        this.selectAllEdit = allLanguagesSelected;
      }
    },

    goBack() {
      this.$router.push("/articlecategory");
    },

    onMoveTopChange() {
      if (this.cat.moveTop) {
        this.cat.moveBottom = false;
      } else if (!this.cat.moveBottom) {
        this.cat.moveBottom = true;
      }
    },
    onMoveBottomChange() {
      if (this.cat.moveBottom) {
        this.cat.moveTop = false;
      } else if (!this.cat.moveTop) {
        this.cat.moveBottom = true;
      }
    },

    assignYourselfAsAuthor() {
      const user = JSON.parse(localStorage.getItem("user"));
      this.cat.authorName = user.name || "";
      this.cat.authorImage = user.image || "";
      this.$forceUpdate();
    },

    clearAuthor() {
      this.$data.cat.authorName = "";
      this.$data.cat.authorImage = "";
      this.$forceUpdate();
    },

    truncateName(name, length = 15) {
      return name.length > length ? name.substring(0, length) + "..." : name;
    },

    addBlock() {
      if (this.cat.descriptionBlocks.length < 4) {
        this.cat.descriptionBlocks.push({ adType: "", description: "", image: "", adUrl: "" });
      }
    },

    removeBlock(index) {
      if (this.cat.descriptionBlocks.length > 1) {
        this.cat.descriptionBlocks.splice(index, 1);
      }
    },

    formatDate(date) {
      return moment(date).format("DD-MM-YYYY");
    },

    sortBy(field) {
      if (this.sortByField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortByField = field;
        this.sortDirection = "asc";
      }
      this.articleList = this.sortedData;
    },

    updateAdDisplayImage(item, index) {
      if (item) {
        this.cat.descriptionBlocks[index].image = item.image;
        this.cat.descriptionBlocks[index].adUrl = item.link;
      }
    },

    onImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.cat.image = file;
        this.displayImage = URL.createObjectURL(file);
      }
    },

    onNotificationImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.cat.notificationImage = file;
        this.notificationImage = URL.createObjectURL(file);
      } else {
        this.notificationImage = "";
      }
    },

    clearImage() {
      this.displayImage = "";
      this.cat.image = "";
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    clearNotificationImage() {
      this.notificationImage = "";
      this.cat.notificationImage = "";
      if (this.$refs.featureFileInput) {
        this.$refs.featureFileInput.value = "";
      }
    },

    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    triggerNotificationFileInput() {
      if (this.$refs.featureFileInput) this.$refs.featureFileInput.click();
    },

    onDropImage(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onImageChange({ target: { files } });
      }
    },

    onDropImageNotification(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onNotificationImageChange({ target: { files } });
      }
    },

    viewDesc(item) {
      this.$data.viewPopup = true;
      if (item.descriptionBlocks) {
        this.$data.desc = item.descriptionBlocks
          .map((block, index) => {
            return `<div>${block.description}</div>`;
          })
          .join("<br>");
      } else {
        this.$data.desc = item.description;
      }
    },

    async addingNewItem() {
      this.$data.defaultalert = true;
      this.$data.isNew = true;
      const user = JSON.parse(localStorage.getItem("user"));
      this.$data.cat = {
        status: true,
        language: "EN",
        position: "",
        name: "",
        image: "",
        relatedArticles: [],
        // relatedItems: [],
        notification: true,
        publishDate: "",
        publishTime: "12:00 AM",
        description: "",
        notificationImage: "",
        articlecategory: this.$data.selectedCategory._id,
        moveBottom: true,
        moveTop: false,
        descriptionBlocks: [{ adType: "", description: "", image: "", adUrl: "", shopItem: "" }],
        authorName: user.name || "",
        authorImage: user.image || "",
        languagesToTranslate: ["EN"],
      };
      this.selectAllEdit = false;
      this.selectAllLang = false;
      this.$data.displayImage = "";
      this.$data.notificationImage = "";
      if (this.$route.query.fromSelection === "true") {
        const queryParams = { ...this.$route.query };
        delete queryParams.fromSelection;
        this.$router.replace({ query: queryParams });
      }
    },

    async editingItem(i) {
      this.clearImage();
      this.clearNotificationImage();
      await this.fileterusingLanguage(i.language, i);
      this.$data.cat = JSON.parse(JSON.stringify(i));
      this.selectAllEdit = false;
      this.selectAllLang = false;
      this.$data.cat.languagesToTranslate = ["EN"];
      this.$data.displayImage = i.image || "";
      this.$data.cat.publishTime = "12:00 AM";
      this.$data.notificationImage = i.notificationImage || "";
      this.$data.isNew = false;
      this.$forceUpdate();
      this.$data.defaultalert = true;
    },

    async sortPositions() {
      const catId = this.$data.cat2 !== "" ? this.$data.cat2 : this.$route.query.catid;

      this.$vs.loading();
      try {
        const r = await api.put(`${API.ARTICLES_SORT_POSITION}?language=${this.$data.search.language}&cat=${catId}`, {});

        if (r.status === 200) {
          this.$vs.notify({
            text: "Sorted Successfully",
            color: "success",
            position: "top-right",
          });
          this.searchData();
        }
      } catch (error) {
        this.$vs.notify({
          text: error.response.data.error,
          color: "danger",
          position: "top-right",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async updatePosition(item, type) {
      this.$vs.loading();
      try {
        const r = await api.put(`${API.ARTICLES_UPDATE_POSITION}/${item._id}?direction=${type}&cat=${item.articlecategory._id}`, {});

        if (r.status === 200) {
          this.$vs.notify({
            text: "Position Updated Successfully",
            color: "success",
            position: "top-right",
          });
          await this.searchData();
        }
      } catch (error) {
        console.log(error);
        this.$vs.notify({
          text: error.response.data.error,
          color: "danger",
          position: "top-right",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async create() {
      if (this.isNew && this.cat.languagesToTranslate.length > 1) return this.createAndTranslate();

      const missingFields = [];
      if (!this.$data.cat.language) missingFields.push("Language");
      if (!this.$data.cat.name) missingFields.push("Name");
      if (!this.$data.displayImage) missingFields.push("Image");
      if (this.$data.cat.descriptionBlocks) {
        if (!this.$data.cat.descriptionBlocks || !this.$data.cat.descriptionBlocks.length) missingFields.push("Description Blocks");
        if (this.$data.cat.descriptionBlocks[0].description.trim() == "") missingFields.push("1 Description Block");
      }
      if (!this.$data.cat.publishDate) missingFields.push("Publish Date");
      if (!this.$data.cat.publishTime) missingFields.push("Publish Time");

      const publishTime = moment(this.$data.cat.publishTime, "hh:mm A");
      if (publishTime.minutes() % 15 !== 0) {
        this.$vs.notify({
          text: "Publish Time must be in 15-minute intervals.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      if (missingFields.length) {
        this.$vs.notify({
          text: `Please enter the following fields: ${missingFields.join(", ")}`,
          color: "danger",
          position: "top-right",
        });
        return;
      }

      try {
        this.$vs.loading();
        if (this.$data.cat.image instanceof File) await this.uploadImage({ target: { files: [this.$data.cat.image] } }, 1);
        if (this.$data.cat.notificationImage instanceof File) await this.uploadImage({ target: { files: [this.$data.cat.notificationImage] } }, 2);

        var relatedArt = [];
        // var relatedIts = [];
        this.$data.cat.image = this.$data.displayImage;
        this.$data.cat.notificationImage = this.$data.notificationImage;

        if (this.$data.cat.relatedArticles.length > 0) {
          await Promise.all(
            this.$data.cat.relatedArticles.map(async (item) => {
              if (item._id) relatedArt.push(item._id);
            })
          );
        }

        this.showLoader = true;
        let apiToCall = API.ARTICLE;
        let data = this.$data.cat;

        if (!this.$data.isNew) {
          apiToCall = API.ARTICLE;
          data["image"] = this.$data.displayImage;
          data["notificationImage"] = this.$data.notificationImage;
          data["relatedArticles"] = relatedArt;
          data["descriptionBlocks"] = this.$data.cat.descriptionBlocks;

          const res = await api.put(`${apiToCall}?id=${data._id}`, data);
          if (res.status === 200) await this.searchData();
        } else {
          if (!this.$data.isNew && !this.$data.clone) {
            this.$data.displayImage = "";
            this.$data.notificationImage = "";
          }

          if (!this.$data.cat.moveTop && !this.$data.cat.moveBottom) {
            this.$data.cat.moveBottom = true;
          }
          data["descriptionBlocks"] = this.$data.cat.descriptionBlocks;

          const res = await api.post(apiToCall, data);
          if (res.status === 200) {
            await this.searchData();
            this.$data.defaultalert = false;
            this.$vs.notify({
              text: "Saved Successfully",
              color: "success",
              position: "top-right",
            });
          }
        }
        this.$data.defaultalert = false;
      } catch (error) {
        console.log(error);
        this.$vs.notify({
          text: "Error Saving",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
        this.showLoader = false;
      }
    },

    async createAndTranslate() {
      const missingFields = [];
      if (!this.$data.cat.language) missingFields.push("Language");
      if (!this.$data.cat.name) missingFields.push("Name");
      if (!this.$data.displayImage) missingFields.push("Image");
      if (this.$data.cat.descriptionBlocks) {
        if (!this.$data.cat.descriptionBlocks || !this.$data.cat.descriptionBlocks.length) missingFields.push("Description Blocks");
        if (this.$data.cat.descriptionBlocks[0].description.trim() == "") missingFields.push("1 Description Block");
      }
      if (!this.$data.cat.publishDate) missingFields.push("Publish Date");
      if (!this.$data.cat.publishTime) missingFields.push("Publish Time");

      const publishTime = moment(this.$data.cat.publishTime, "hh:mm A");
      if (publishTime.minutes() % 15 !== 0) {
        this.$vs.notify({
          text: "Publish Time must be in 15-minute intervals.",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      if (missingFields.length) {
        this.$vs.notify({
          text: `Please enter the following fields: ${missingFields.join(", ")}`,
          color: "danger",
          position: "top-right",
        });
        return;
      }

      try {
        if (this.$data.cat.image instanceof File) await this.uploadImage({ target: { files: [this.$data.cat.image] } }, 1);
        if (this.$data.cat.notificationImage instanceof File) await this.uploadImage({ target: { files: [this.$data.cat.notificationImage] } }, 2);

        var relatedArt = [];
        // var relatedIts = [];
        this.$data.cat.image = this.$data.displayImage;
        this.$data.cat.notificationImage = this.$data.notificationImage;

        if (this.$data.cat.relatedArticles.length > 0) {
          await Promise.all(
            this.$data.cat.relatedArticles.map(async (item) => {
              if (item._id) relatedArt.push(item._id);
            })
          );
        }

        let apiToCall = API.ARTICLE_CANDT;
        let data = this.$data.cat;
        this.$data.showLoader = true;

        if (!this.$data.isNew && !this.$data.clone) {
          apiToCall = API.UANDT;
          data["image"] = this.$data.displayImage;
          data["notificationImage"] = this.$data.notificationImage;
          data["relatedArticles"] = relatedArt;
          data["descriptionBlocks"] = this.$data.cat.descriptionBlocks;

          this.$data.defaultalert = false;
          this.$data.showLoader = true;

          const res1 = await api.put(apiToCall + "?id=" + data._id, data);
          if (res1.status === 200) {
            await this.searchData();
            this.$data.defaultalert = false;
            this.$vs.notify({
              text: "Saved Successfully",
              color: "success",
              position: "top-right",
            });
          }
          return;
        }

        if (!this.$data.isNew && !this.$data.clone) {
          this.$data.displayImage = "";
          this.$data.notificationImage = "";
        }

        if (this.$data.clone) {
          apiToCall = API.ARTICLE_CANDT;

          data = {
            name: this.$data.cat.name,
            position: this.$data.cat.position,
            language: this.$data.cat.language,
            status: this.$data.cat.status,
            image: this.$data.cat.image,
            description: this.$data.cat.description,
            publishDate: this.$data.cat.publishDate,
            publishTime: this.$data.cat.publishTime,
            notification: this.$data.cat.notification,
            relatedArticles: relatedArt,
            notificationImage: this.$data.cat.notificationImage,
            articlecategory: this.$data.selectedCategory._id,
            authorName: this.$data.cat.authorName,
            authorImage: this.$data.cat.authorImage,
          };
          data["descriptionBlocks"] = this.$data.cat.descriptionBlocks;
        }

        this.$data.defaultalert = false;
        this.$data.showLoader = true;

        const res2 = await api.post(apiToCall, data);
        if (res2.status === 200) await this.searchData();
        this.$data.defaultalert = false;
        this.$vs.notify({
          text: "Saved Successfully",
          color: "success",
          position: "top-right",
        });
      } catch (error) {
        console.log(error);
        this.$vs.notify({
          text: "Error Saving",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
        this.$data.showLoader = false;
      }
    },

    fetchAffiliateItems() {
      this.$vs.loading();
      // api.get("https://shop.exaweb.in:5200/affiliate/allItems")
      api
        .get("https://api.exaweb.in:5200/affiliate/allItems")
        .then((response) => {
          this.$vs.loading.close();
          this.$data.selectedItems = response.data.map((item) => ({
            name: item.title,
            value: item._id,
            image: item.images[0],
            link: item.link,
          }));
        })
        .catch((error) => {
          console.error("Error fetching affiliate items:", error);
          this.$vs.loading.close();
        });
    },

    async deleteCategory() {
      this.$vs.loading();
      try {
        const r = await api.delete(API.ARTICLE + "?id=" + this.$data.itemToDelete._id, {});
        if (r.status === 200) {
          await this.searchData();
          this.$vs.notify({
            text: "Deleted Successfully",
            color: "success",
            position: "top-right",
          });
        }
      } catch (error) {
        this.$vs.notify({
          text: "Error Deleting",
          color: "danger",
          position: "top-right",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
        this.deleteArticlePopup = false;
        this.showLoader = false;
      }
    },

    async searchData() {
      try {
        const page = !isNaN(this.currentPage) && this.currentPage > 0 ? this.currentPage : 1;

        const res = await api.get(
          `${API.ARTICLE_SEARCH}?articlecategory=${this.$route.query.catid}&language=${this.$data.search.language}&sort=${this.$data.search.sort}&status=${this.$data.search.status}&page=${page}&limit=${this.limit}&combineViews=${this.$data.search.combineViews}`
        );

        if (res.status === 200 && res.data.data && res.data.data.length > 0) {
          this.$data.articleList = res.data.data;

          this.totalPages = Math.ceil(res.data.totalNumber / this.limit);

          this.$data.cat2 = res.data.data[0].articlecategory._id;
        } else this.$data.articleList = [];
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.$vs.loading.close();
      }
    },

    async uploadImageAd(data, flag) {
      const imageType = ["image/jpeg", "image/jpg", "image/webp"];
      var isValid = false;
      isValid = imageType.indexOf(data.target.files[0].type) > -1;

      if (!isValid) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "Only JPEG,JPG,WEBP Image files formats should be supported",
          color: "danger",
          position: "top-right",
        });
        this.$refs.fileInput.value = null;
        throw new Error("Invalid file type");
      }
      if (data.target.files[0].size > 500000) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "File size should be less than 500kb",
          color: "danger",
          position: "top-right",
        });
        throw new Error("File size exceeds limit");
      }

      let formdata = new FormData();

      formdata.append("adType", this.$data.cat.descriptionBlocks[flag].adType);
      formdata.append("file", data.target.files[0]);
      this.$vs.loading();
      try {
        const res = await api.post(API.UPLOAD_IMAGE, formdata, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        if (res.status == 200) {
          this.$data.cat.descriptionBlocks[flag].image = res.data.files[0].url;
          this.$vs.notify({
            text: "File Uploaded",
            color: "success",
            position: "top-right",
          });
          this.$vs.loading.close();
        }
      } catch (error) {
        this.$vs.loading.close();
        this.$vs.notify({
          text: "File Uploading Failed",
          color: "danger",
          position: "top-right",
        });
      }
    },

    async uploadImage(files, flag) {
      try {
        const imageType = ["image/jpeg", "image/jpg", "image/webp"];
        var isValid = false;
        isValid = imageType.indexOf(files.target.files[0].type) > -1;

        if (!isValid) {
          this.$vs.notify({
            title: "Uploading Error",
            text: "Only JPEG,JPG,WEBP Image files formats should be supported",
            color: "danger",
            position: "top-right",
          });
          this.$refs.fileInput.value = null;
          throw new Error("Invalid file type");
        }
        if (files.target.files[0].size > 500000) {
          this.$vs.notify({
            title: "Uploading Error",
            text: "File size should be less than 500kb",
            color: "danger",
            position: "top-right",
          });
          throw new Error("File size exceeds limit");
        }

        let formdata = new FormData();
        if (flag === 1) {
          this.$data.fileName = files.target.files[0].name;
        } else if (flag === 2) {
          this.$data.notificationfileName = files.target.files[0].name;
        } else {
          this.$data.authorImageFileName = files.target.files[0].name;
        }

        formdata.append("category", this.$data.cat.name);
        formdata.append("file", files.target.files[0]);
        this.$vs.loading();

        const response = await api.post(API.UPLOAD_IMAGE, formdata, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const data = response;
        if (flag === 1) {
          this.$data.displayImage = response.data.files[0].url;
        } else if (flag === 2) {
          this.$data.notificationImage = response.data.files[0].url;
        } else {
          this.$data.cat.authorImage = response.data.files[0].url;
        }

        if (response) {
          this.$vs.notify({
            text: "File Uploaded",
            color: "success",
            position: "top-right",
          });
          this.$vs.loading.close();
        }
        this.$refs.fileInput.value = null;
        this.$emit("input", data);

        this.$vs.loading.close();
      } catch (error) {
        this.$vs.notify({
          text: "File Uploading Failed",
          color: "danger",
          position: "top-right",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    deleteImage(flag) {
      if (flag == 1) {
        this.$data.displayImage = "";
      } else if (flag == 2) {
        this.$data.notificationImage = "";
      } else {
        this.$data.cat.authorImage = "";
      }
    },

    async resetData() {
      this.$data.search = {
        sort: "ASC",
        language: "EN",
        status: "0",
      };
      this.$data.limit = 25;
      this.$data.currentPage = 1;
      await this.searchData();
    },

    async getPlainData(cat, lang) {
      try {
        const relatedList = await api.get(`${API.ARTICLE}/plain?language=${lang}&sort=${this.$data.search.sort}&status=${this.$data.search.status}`);

        if (relatedList.status == 200) {
          this.$data.languageArticleList = relatedList.data.data;
        } else {
          this.$data.languageArticleList = [];
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },

    async fileterusingLanguage(event, item) {
      try {
        // if (event == "EN") {
        //   return;
        // }
        let id = item.ref || item._id;
        if (event && id) {
          this.$vs.loading();
          const res = await api.get(API.ARTICLE + "/sin/" + id, { params: { language: event } });
          if (res.status == 200) {
            // const artCat = res.data.data.articlecategory._id;
            // await this.getPlainData(artCat, event);

            this.$data.cat = Object.assign(this.$data.cat, res.data.data);
            this.$data.displayImage = res.data.data.image;
            this.$data.notificationImage = res.data.data.notificationImage;
          } else {
            this.$data.cat = Object.assign({}, this.$data.enData);
          }
        }
      } catch (error) {
        this.$data.cat = Object.assign({}, this.$data.enData);
        console.error("Error try again.:", error);
        this.$vs.notify({
          title: "Error",
          text: "Error try again.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async updateStatus(id, currentStatus) {
      this.$vs.loading();
      try {
        const newStatus = !currentStatus;

        const r = await api.put(`${API.ARTICLE}/disable?id=${id}`, { status: newStatus });
        if (r.status === 200) {
          await this.searchData();
          this.$vs.notify({
            title: "Success",
            text: `Article ${newStatus ? "disabled" : "enabled"} successfully`,
            color: "success",
          });
        }
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: error.message || "Failed to update status",
          color: "danger",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchSingleCategory(id) {
      this.$vs.loading();
      try {
        const r = await api.get(`${API.ARTICLE_CATEGORY}/single/${id}`);
        if (r.status === 200) {
          this.$data.selectedCategory = r.data.data;
        }
      } catch (error) {
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchAllLanguages() {
      this.$vs.loading();
      try {
        const r = await api.get(API.GET_LANG, {});
        this.languages = r.data;
      } catch (error) {
        this.$vs.notify({
          text: "Error fetching languages",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },
  created() {
    if (this.$data.search.language) {
      this.$data.search.language = this.$route.query.catLang;
    }

    if (!this.$data.search.language) {
      this.$data.search.language = "all";
    }
  },
  async mounted() {
    if (this.$route.query.catid) {
      await Promise.all([this.fetchAllLanguages(), this.fetchSingleCategory(this.$route.query.catid), this.searchData(), this.fetchAffiliateItems()]);
    } else {
      this.$router.push("/articlecategory");
    }
    if (this.$route.query.fromSelection === "true") this.addingNewItem();
    this.getPlainData(this.$route.query.catid, "EN");
  },
};
</script>

<style scoped>
.material-icons {
  font-family: "Material Icons";
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}
</style>
